import type { Camadas, ModoVisualizacao } from '../visualizacao3d/componentes/contextoMaquete'

export interface PresetModo {
  rotulo: string
  descricao: string
  camadas: Camadas
  opacidadeCarroceria: number
  /**
   * Rótulos flutuantes na cena. Começam desligados em todos os módulos: os
   * chips sobrepostos cobriam o veículo e competiam com vetores, rastros e
   * ferramentas — quem quiser nomear peças religa em Camadas.
   */
  mostrarRotulos: boolean
}

const camadas = (parcial: Partial<Camadas>): Camadas => ({
  estrutura: false,
  zonaDeformacao: false,
  srs: false,
  retencao: false,
  carroceria: true,
  habitaculo: true,
  // mecânica e energia são muitas peças e só interessam quando a aula é sobre
  // elas; entram desligadas e o aluno liga
  mecanica: false,
  energia: false,
  vitima: true,
  ...parcial,
})

/**
 * Cada modo é um preset de camadas, não uma cena diferente.
 *
 * O aluno pode continuar ligando e desligando camadas depois de escolher o
 * modo: o preset só define o ponto de partida útil.
 */
export const PRESETS: Record<ModoVisualizacao, PresetModo> = {
  externo: {
    rotulo: 'Externo',
    descricao: 'Veículo completo, como o aluno o encontra na cena.',
    camadas: camadas({}),
    opacidadeCarroceria: 1,
    mostrarRotulos: false,
  },
  estrutura: {
    rotulo: 'Estrutura',
    descricao: 'Carroceria translúcida, gaiola estrutural e zonas de absorção em evidência.',
    camadas: camadas({ estrutura: true, zonaDeformacao: true }),
    opacidadeCarroceria: 0.14,
    mostrarRotulos: false,
  },
  srs: {
    rotulo: 'SRS',
    descricao: 'Cintos, pretensionadores e airbags do perfil didático selecionado.',
    // a gaiola em vermelho abafa o azul dos airbags e o laranja do cinto;
    // o aluno religa a estrutura quando quiser discutir os dois juntos
    camadas: camadas({ srs: true, retencao: true }),
    opacidadeCarroceria: 0.12,
    mostrarRotulos: false,
  },
  tabletop: {
    rotulo: 'Table top',
    descricao: 'Construa livremente a ocorrência com veículos, vítimas, estabilização e vidros — tudo em uma cena só.',
    camadas: camadas({}),
    opacidadeCarroceria: 1,
    mostrarRotulos: false,
  },
  cinematica: {
    rotulo: 'Cinemática',
    // descrição da ABA, não de um tipo. Ficou nomeando a frontal desde quando era
    // o único tipo, e passou a contradizer a tela: com o capotamento escolhido, o
    // painel abria dizendo "colisão frontal"
    descricao:
      'O veículo muda de velocidade, o ocupante continua o movimento que tinha, e a retenção é o que acopla um ao outro.',
    camadas: camadas({ srs: true, retencao: true, zonaDeformacao: true }),
    opacidadeCarroceria: 0.16,
    mostrarRotulos: false,
  },
  estabilizacao: {
    rotulo: 'Estabilização',
    descricao: 'Posição final, preenchimento de vazios e estabilização primária e secundária.',
    camadas: camadas({ estrutura: true }),
    opacidadeCarroceria: 0.48,
    mostrarRotulos: false,
  },
  extracao: {
    rotulo: 'Extração',
    descricao: 'Rotas de retirada, arco do ângulo e rotação do corpo.',
    // a gaiola estrutural compete com a rota e o arco: fica desligada aqui,
    // e o aluno pode religá-la para discutir o vão de saída
    camadas: camadas({}),
    opacidadeCarroceria: 0.12,
    mostrarRotulos: false,
  },
  vidros: {
    rotulo: 'Manejo de vidros',
    descricao: 'Oficina prática de gestão de vidros (laminado vs temperado), contenção de resíduos e proteção da vítima.',
    camadas: camadas({ habitaculo: true, vitima: true, carroceria: true }),
    opacidadeCarroceria: 0.75,
    mostrarRotulos: false,
  },
  interior: {
    rotulo: 'Interior',
    descricao: 'Bancos, volante, pedais, painel e assoalho do habitáculo.',
    camadas: camadas({ habitaculo: true, retencao: true }),
    opacidadeCarroceria: 0.12,
    mostrarRotulos: false,
  },
  mecanica: {
    rotulo: 'Mecânica',
    descricao: 'Conjunto motriz, motor, transmissão e suspensão.',
    camadas: camadas({ mecanica: true, estrutura: true }),
    opacidadeCarroceria: 0.12,
    mostrarRotulos: false,
  },
  energia: {
    rotulo: 'Energia',
    descricao: 'Bateria de 12V, alta tensão, cabeamento laranja, linhas de combustível e GNV.',
    camadas: camadas({ energia: true, estrutura: true }),
    opacidadeCarroceria: 0.14,
    mostrarRotulos: false,
  },
}

export const MODOS_MAQUETE: ModoVisualizacao[] = [
  'externo',
  'estrutura',
  'interior',
  'srs',
  'mecanica',
  'energia',
]

/**
 * Modo conceito: só o veículo em silhueta, a vítima e o que atrapalha a rota.
 *
 * Pedido do instrutor — para o aluno entender o conceito, tudo que não é o
 * conceito precisa sair da tela. Os obstáculos continuam visíveis porque o
 * alerta da rota força visibilidade independente das camadas.
 */
export const PRESET_CONCEITO: Pick<PresetModo, 'camadas' | 'opacidadeCarroceria' | 'mostrarRotulos'> = {
  camadas: camadas({ habitaculo: false }),
  opacidadeCarroceria: 0.06,
  mostrarRotulos: false,
}

export const ROTULOS_CAMADA: Record<keyof Camadas, string> = {
  estrutura: 'estrutura / reforços',
  zonaDeformacao: 'zonas de absorção',
  srs: 'SRS / airbags',
  retencao: 'cinto / pretensionador',
  carroceria: 'carroceria',
  habitaculo: 'interior',
  mecanica: 'mecânica',
  energia: 'energia / riscos',
  vitima: 'vítima',
}
